/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import { Tooltip } from "bootstrap";

import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

import { Images } from "./Images";
import CustomDatePicker from "../Common/CustomDatePicker";
import CustomSearchableDropdown from "../Common/CustomSearchableDropdown";
import CustomSearchableDropdownTickmark from "../Common/CustomSearchableDropdownTickmark";
import { CreateNewMeeting_api, CreateNewMeetingTimeGrid_api, GetParticipantsForMeeting_api } from "../Common/CommonAPI";
import MeetingStepProgress from "./MeetingStepProgress";
import { SpotAvailability_api } from "../NewBooking/NewBookingAPI";
import { handleApiResponse } from "../../hooks/notificationHelper";
import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";
import { useApiResponseHandler } from "../../hooks/useApiResponseHandler";
import useMeetingRecurrence from "../../hooks/useMeetingRecurrence.js";
import useMeetingNameTooltip from "../../hooks/useMeetingNameTooltip.js";
import { getEmailID, getFirstname, getLastname, getLoginId } from "../../utils/SecurityService";
import { delay, formatDate, formatDateToLong, formatReactString, getEndDateBy, isPastTimeInterval, EMAIL_PATTERN, MEETING_NAME_MAX_LENGTH, WEEKDAY_LONG_KEYS } from "../../utils/CommonUtils";
import NotificationMessage from "./NotificationMessage";

// Three visible steps (details, room, participants) plus the confirmation and
// approval screens, which share the last progress circle. Time is picked on
// DETAILS from the zone's points, so ROOM only lists rooms free at that time;
// the day grid beside ROOM onwards can then refine it.
const SECTION = {
  DETAILS: 0,
  ROOM: 1,
  PARTICIPANTS: 2,
  CONFIRM: 3,
  APPROVAL: 4
};

// Circles in this screen's progress bar. Passed explicitly because
// NewMeetingModal shares MeetingStepProgress (and activeTab="meeting") but
// still has two steps.
const MEETING_PROGRESS_STEPS = 3;

// SpotType the meeting time-grid endpoint expects. Mirrors NewMeetingModal,
// which hardcodes the same 3 for meeting rooms.
const MEETING_SPOT_TYPE = 3;

const BookMeeting = ({ selectedTab, step, setStep, bookingInfo = {}, needApproval, setNeedApproval, setMeetingStep, setZoneCreditValue, timeGridContainer = null }) => {
  const { t, i18n } = useTranslation();

  const calendarRef = useRef(null);
  const meetingNameRef = useRef(null);

  const history = useHistory();
  const FirstName = getFirstname();
  const LastName = getLastname();
  const memberEmail = getEmailID();
  const PId = getLoginId();
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  const [showTooltip, setShowTooltip] = useState(false);

  const [errors, setErrors] = useState({
    meetingName: "",
    timeRequired: "",
    timeZero: "",
    timeInvalid: "",
    endDate: "",
    recurrenceDay: ""
  });
  // 🔹 Basic State
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const [selectedFrom, setSelectedFrom] = useState("");
  const [selectedTo, setSelectedTo] = useState("");
  const [maxDate, setMaxDate] = useState(null);

  const [showSlots, setShowSlots] = useState(false);
  // Which half of the From/To box the next time-point click fills.
  const [activeField, setActiveField] = useState(null);
  const [timePoints, setTimePoints] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [zoneCurrentDate, setZoneCurrentDate] = useState("");
  const [zoneCurrentTime, setZoneCurrentTime] = useState("");

  // 🔹 Meeting Fields
  const [meetingName, setMeetingName] = useState("");
  const [notes, setNotes] = useState("");
  const [endDate, setEndDate] = useState(null);
  //const [recurrence, setRecurrence] = useState("");
  const [currentSection, setCurrentSection] = useState(0);

  // GuestParticipants: for create new meeting API
  const [externalGuests, setExternalGuests] = useState([]);
  const [guestInput, setGuestInput] = useState({
    name: "",
    surname: "",
    email: "",
    nationalIdentificationId: ""
  });

  const [meetingRoomData, setMeetingRoomData] = useState(null);

  // Participants: for create new meeting API (initial sample entries moved into state so we can append/remove)
  const [participants, setParticipants] = useState([]);
  const [checkedParticipants, setCheckedParticipants] = useState([]);
  const [showGuestForm, setShowGuestForm] = useState(false);

  // Recurrence values, labels and payload are shared with NewMeetingModal via
  // useMeetingRecurrence - the two create-meeting flows must stay in step on
  // what they send. Only the markup below is this screen's own. End date and
  // the repeat toggle stay local: each screen resets them on its own terms.
  const {
    repeatType,
    repeatInterval,
    weeklyRepeatOption,
    setRepeatInterval,
    repeatIntervalOptions,
    monthlyRecurrenceChoices,
    activeMonthlyChoiceId,
    showsWeekdayChips,
    changeRepeatType,
    toggleWeeklyDay,
    isWeeklyDaySelected,
    selectMonthlyChoice,
    resetRecurrence,
    getRecurrencePayload
  } = useMeetingRecurrence({ selectedDate });

  const [repeatOptionsList, setRepeatOptionsList] = useState([]);
  const [weeklyOptionsList, setWeeklyOptionsList] = useState([]);

  const [isRepeatEnabled, setIsRepeatEnabled] = useState(false);

  // 🔹 Selected Zone Object
  const selectedZoneObj = useMemo(() => {
    return bookingInfo?.ZoneList?.find(
      z => String(z.Id) === String(selectedZone)
    );
  }, [selectedZone, bookingInfo]);

  const spotTagList = useMemo(() => {
    if (!selectedZoneObj) return [];

    return selectedZoneObj.Tags || [];
  }, [selectedZoneObj]);

  const toggleTag = (tagId) => {
    setSelectedTags((prev) => {
      if (prev.includes(tagId)) {
        return prev.filter((id) => id !== tagId);
      }

      return [...prev, tagId];
    });
  };

  const zoneCreditValue = Number(selectedZoneObj?.ZoneCreditValue ?? 0);
  useEffect(() => {
    if (setZoneCreditValue) {
      setZoneCreditValue(zoneCreditValue);
    }
  }, [zoneCreditValue]);

  const [isFullyDayBlocked, setIsFullyDayBlocked] = useState(0);
  const [isRoomHasApprover, setIsRoomHasApprover] = useState(0);
  const isDisableOnSpotApi = isFullyDayBlocked === 1 || (isRoomHasApprover === 1 && isFullyDayBlocked === 1);

  const [capacity, setCapacity] = useState(0);

  const displaySelectedDate =
    selectedDate instanceof Date && !isNaN(selectedDate.getTime())
      ? `${String(selectedDate.getDate()).padStart(2, "0")} ${t(
        selectedDate.toLocaleString("en-GB", { month: "long" })
      )} ${selectedDate.getFullYear()}`
      : "";

  const { handleResponseCode, showAlertPopupModal, alertPopupModalData, showErrorPopupModal,
    errorPopupModalData, setShowAlertPopupModal, setShowErrorPopupModal } = useApiResponseHandler();
  const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);
  // #region Handle Errors- start

  const meetingNamePattern = useMemo(() => {
    const raw = t("react_meeting_name_regex");
    // i18next returns the key itself when missing — guard against that.
    if (raw && raw !== "react_meeting_name_regex") {
      try {
        return new RegExp(raw);
      } catch {
        // malformed pattern in a locale file → fall through to default
      }
    }
    return /^(?!.*÷)[0-9a-zA-ZÀ-ÿĀ-ſ &,?\\'.()’-]+$/;
  }, [t, i18n.language]);

  // Validates the given section. Time is re-checked on ROOM and PARTICIPANTS
  // because the grid there can change it, and a room switch can clear it.
  const validateSection = (section) => {
    const checksDetails = section === SECTION.DETAILS;
    const checksRoom = section === SECTION.ROOM;

    let newErrors = {
      meetingName: "",
      room: "",
      timeRequired: "",
      timeZero: "",
      timeInvalid: "",
      endDate: "",
      recurrenceDay: "",
      firstname: "",
      lastname: "",
      email: ""
    };

    let isValid = true;

    // 1️⃣ Meeting Name
    if (checksDetails) {
      if (!meetingName || meetingName.trim() === "") {
        newErrors.meetingName = t("react_meeting_name_is_required");
        isValid = false;
      } else if (!meetingNamePattern.test(meetingName.trim())) {
        newErrors.meetingName = t("only_alphanumeric_characters_allowed");
        isValid = false;
      } else if (meetingName.length > MEETING_NAME_MAX_LENGTH) {
        newErrors.meetingName = t("meeting_name_maximum_length_is_s_characters")
          .replace("%s", MEETING_NAME_MAX_LENGTH);
        isValid = false;
      }
    }

    // 2️⃣ Room. Its own step again, so a booking cannot reach the participants
    // step without one.
    if (checksRoom && !selectedSpot) {
      // Existing keys only - no new strings, which would ship English-only
      // until the next Lokalise push.
      newErrors.room = spotsLoading
        ? t("react_loading")
        : t("no_space_available");
      isValid = false;
    }

    // 3️⃣ Only FROM selected
    if (selectedFrom && !selectedTo) {
      newErrors.timeInvalid = t("react_select_end_time");
      isValid = false;
    }

    // 4️⃣ Only TO selected
    else if (!selectedFrom && selectedTo) {
      newErrors.timeInvalid = t("react_select_start_time");
      isValid = false;
    }

    // 5️⃣ Neither selected. Required on step 1: the room list on step 2 is
    // filtered by this time, so there is nothing to list without it.
    else if (
      !selectedFrom ||
      !selectedTo ||
      selectedFrom === "00:00" ||
      selectedTo === "00:00"
    ) {
      newErrors.timeInvalid = t("please_select_time");
      isValid = false;
    }

    // 6️⃣ Both selected → validate range
    else if (selectedFrom >= selectedTo) {
      newErrors.timeInvalid = t("end_time_must_be_after_start_time");
      isValid = false;
    }

    // 7️⃣ Already over today. Caught here rather than on step 2, where the
    // grid's availability check would otherwise clear it.
    else if (hasRangeEnded(selectedFrom, selectedTo)) {
      newErrors.timeInvalid = t("The_end_time_must_not_be_in_the_past");
      isValid = false;
    }

    if (checksDetails && repeatType !== 0 && isRepeatEnabled && !endDate) {
      newErrors.endDate = t("please_select_end_date");
      isValid = false;
    }

    if (checksDetails && showsWeekdayChips && weeklyRepeatOption.length === 0) {
      newErrors.recurrenceDay = t("please_select_minimum_one_day");
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // #endregion Handle Errors- end

  const isTimeSelected =
    selectedFrom !== "00:00" &&
    selectedTo !== "00:00";

  // Date changes still reset the booking flow broadly.
  useEffect(() => {
    setSelectedFrom("");
    setSelectedTo("");
    setSelectedTags([]);
    setCurrentSection(0);
    setShowSlots(false);
    setNotes("");
    setEmployee("");

    setGuestInput({ name: "", surname: "", email: "" });
    setParticipants([]);
    setSelectedEmployees([]);
    setExternalGuests([]); // clear previous external guests
    setCurrentSection(SECTION.DETAILS);

    resetRecurrence();
    setIsRepeatEnabled(false);
    setEndDate(null);
  }, [selectedDate]);

  // Zone changes preserve the general meeting form values while
  // refreshing the zone-dependent selection state.
  useEffect(() => {
    setSelectedTags([]);
    setShowSlots(false);
    setActiveField(null);
    setEmployee("");
    setSelectedSpot(0);
    setSpotList([]);
    setErrors((prev) => ({
      ...prev,
      timeInvalid: "",
      capacity: ""
    }));
  }, [selectedZone]);


  // #region Interval-Time slot click handler- start
  //
  // Step 1's From/To picker, restored from before the redesign: it offers the
  // zone's time points (selectedZoneObj.TimeInterval), because no room has been
  // chosen yet. Step 2 then lists only rooms free for the picked range, and the
  // day grid from step 2 onwards writes to the same selectedFrom/selectedTo.
  const handleTimeClick = (time) => {
    // 👉 FROM clicked
    if (activeField === "from") {
      setSelectedFrom(time);

      if (selectedTo && time >= selectedTo) {
        setErrors((prev) => ({
          ...prev,
          timeInvalid: t("the_starttime_cant_before")
        }));
      } else {
        setErrors((prev) => ({ ...prev, timeInvalid: "" }));
      }

      setShowSlots(false);
      return;
    }

    // 👉 TO clicked
    if (activeField === "to") {
      if (!selectedFrom) {
        setErrors((prev) => ({
          ...prev,
          timeInvalid: t("react_please_select_from_time_first")
        }));
        return;
      }

      if (time <= selectedFrom) {
        setErrors((prev) => ({
          ...prev,
          timeInvalid: t("end_time_must_be_after_start_time")
        }));
        return;
      }

      setErrors((prev) => ({ ...prev, timeInvalid: "" }));
      setSelectedTo(time);
      setShowSlots(false);
    }
  };

  useEffect(() => {
    setShowSlots(false);
  }, [step]);

  const toMinutes = (time) => {
    const [hours, minutes] = String(time || "00:00").split(":").map(Number);
    return (hours * 60) + minutes;
  };

  const minutesToTime = (totalMinutes) =>
    `${String(Math.floor(totalMinutes / 60)).padStart(2, "0")}:${String(totalMinutes % 60).padStart(2, "0")}`;

  // #endregion Interval-Time slot click handler- end

  // #region Fetch available spots when step 2 is active- start
  const [spotList, setSpotList] = useState([]);
  const [spotsLoading, setSpotsLoading] = useState(false);
  const [selectedSpot, setSelectedSpot] = useState(0);
  const selectedSpotObj = useMemo(() => {
    return spotList.find(
      s => String(s.Id) === String(selectedSpot)
    );
  }, [selectedSpot, spotList]);

  // The zone's time points feed step 1's From/To picker and, as a fallback,
  // the grid's visible range.
  useEffect(() => {
    if (!selectedZoneObj) return;

    setTimePoints(selectedZoneObj.TimeInterval || []);
  }, [selectedZoneObj]);

  useEffect(() => {
    if (!selectedSpotObj) return;

    // ✅ Set basic values
    setIsRoomHasApprover(selectedSpotObj.IsRoomHasApprover || 0);
    setIsFullyDayBlocked(selectedSpotObj.IsFullyDayBlocked || 0);
    setCapacity(selectedSpotObj.Capacity || 0);

    // ✅ Approver logic
    const approvalRequired =
      Number(selectedSpotObj?.IsRoomHasApprover) === 1 &&
      Number(selectedSpotObj?.IsUserApprover) === 0;

    setNeedApproval(approvalRequired);

    if (approvalRequired) {
      const approvalText = selectedSpotObj.ApprovalInfo || "";

      setApprovalInfo(approvalText);
      setApproverList(selectedSpotObj.ApproverList || []);

      const parsed = parseApprovalInfo(approvalText);
      setRequestWindow(parsed.request);
      setReleaseWindow(parsed.release);
    } else {
      setApprovalInfo("");
      setApproverList([]);
      setRequestWindow({ hours: "", minutes: "" });
      setReleaseWindow({ hours: "", minutes: "" });
    }

  }, [selectedSpotObj]);

  const [repeatOptionByLiftRestriction, setRepeatOptionByLiftRestriction] = useState(0);

  // A grid pick on step 2 refetches with the new time; only the latest
  // response may land, or a slow earlier one would restore a stale room list.
  const spotsRequestIdRef = useRef(0);

  const fetchSpots = async () => {

    // Clears the flag handleNext raises on entering the Room step.
    if (!selectedZone) {
      setSpotsLoading(false);
      return;
    }

    const requestId = spotsRequestIdRef.current + 1;
    spotsRequestIdRef.current = requestId;

    try {

      setSpotsLoading(true);

      const params = {
        BookingDate: formatDate(selectedDate),
        ZoneId: selectedZone,
        SelectedTab: selectedTab,   // 1 for parking, 2 for desking

        FilterByStartTime: selectedFrom === "00:00" ? "" : selectedFrom,
        FilterByEndTime: selectedTo === "00:00" ? "" : selectedTo,

        FilterByTags: selectedTags.join(","),

        FilterByBookingPeriod: 3,

        VehicleSizeId: 0,
        AccessibleId: 0,
        ShareableId: 0,
        FuelId: 0,
      };

      const response = await SpotAvailability_api(params);

      if (requestId !== spotsRequestIdRef.current) return;

      /* handleApiResponse(response, setNotification, t, {
        successMsg: 0,
        errorMsg: 1
      }); */
      handleApiUiResponse(response, {
        successToaster: 0,
        errorToaster: 1,
        alertPopup: 1
      });
      if (response?.Data?.ResponseCode !== 200) {
        // Both, or the parent's step and this component's section desync - the
        // parent drives the page title from `step`, this drives the markup.
        setCurrentSection(SECTION.DETAILS);
        setStep(0);
        return;
      }
      const records = response?.Data?.Records || {};
      setZoneCurrentDate(records.ZoneCurrentDate || "");
      setZoneCurrentTime(records.ZoneCurrentTime || "");

      const meetingSpots = records.AvailableMeetingRooms || [];

      const spots =
        meetingSpots.length > 0
          ? meetingSpots
          : [];

      setSpotList(spots);

    } catch (error) {

      console.error("Spot API error", error);

    } finally {

      if (requestId === spotsRequestIdRef.current) {
        setSpotsLoading(false);
      }
    }

  };

  // Fetched only while the Room step is open, filtered by the time picked on
  // step 1 - so the dropdown lists rooms free for that range. Time is a
  // dependency: a new range picked on the grid refreshes the list. The selected
  // room survives the refresh, because the grid refuses ranges booked for it.
  useEffect(() => {
    if (currentSection !== SECTION.ROOM) return;

    fetchSpots();
  }, [currentSection, selectedTags, selectedZone, selectedDate, selectedFrom, selectedTo]);

  useEffect(() => {
    if (spotList.length > 0) {
      const preservedSpot = spotList.find(
        (spot) => String(spot.Id) === String(selectedSpot)
      );
      const nextSpot = preservedSpot || spotList[0];

      setSelectedSpot(nextSpot.Id);

      // OPTIONAL (can remove if using selectedSpotObj effect)
      setCapacity(nextSpot.Capacity || 0);
    } else {
      setSelectedSpot(0); // reset if no spots
    }
  }, [spotList, selectedSpot]);

  // #endregion Fetch available spots- end

  // #region Day time grid (right column)- start
  //
  // A SECOND call, deliberately. SpotAvailability_api drives the Room dropdown
  // because it is the only one that honours the zone's tag filter, but its
  // `BookedSlots` are bare slot strings - it cannot say who booked what. The
  // design labels each block "Meeting room name (Organizer)", and only
  // MeetingRoomListAndInfo carries that (SelectedBookingTime[].MeetingName /
  // .Organizer), so the grid is fed from there.
  //
  // It is fetched per zone+date, not per room: the response already holds every
  // room for that day, so switching room only re-filters what is in hand.
  const [gridRooms, setGridRooms] = useState([]);
  const [gridLoading, setGridLoading] = useState(false);
  const isProgrammaticSelection = useRef(false);
  // FullCalendar fires dateClick after a click-select too; the select handler
  // has already applied the range, so the paired dateClick is skipped.
  const ignoreNextDateClick = useRef(false);

  // The element the grid scrolls inside: hover root for the meeting-name
  // tooltip and its upper bound, as in NewMeetingModal. No aboveModal - this
  // grid is on the page, not inside a Bootstrap modal.
  const [calendarScrollEl, setCalendarScrollEl] = useState(null);
  const getTooltipBoundsEl = useCallback(() => calendarScrollEl, [calendarScrollEl]);

  useMeetingNameTooltip(calendarScrollEl, {
    getBoundsEl: getTooltipBoundsEl,
    titleSelector: ".fc-event-title"
  });

  const fetchMeetingGrid = async () => {
    if (!selectedZone || !selectedDate) return;

    try {
      setGridLoading(true);

      const response = await CreateNewMeetingTimeGrid_api({
        BookingDate: formatDate(selectedDate),
        SpotType: MEETING_SPOT_TYPE,
        ZoneId: selectedZone
      });

      const records = response?.Data?.Records;

      setGridRooms(records?.AvailableRooms || []);

      // The zone's "now", for the past-range check. Fetched from mount, so it is
      // known on step 1 - the room fetch that also carries it only runs on step 2.
      if (records?.ZoneCurrentTime) {
        setZoneCurrentTime(records.ZoneCurrentTime);
        setZoneCurrentDate(records.ZoneCurrentDate || formatDate(new Date()));
      }
    } catch (error) {
      console.error("Meeting time grid error", error);
      setGridRooms([]);
    } finally {
      setGridLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetingGrid();
  }, [selectedZone, selectedDate]);

  const gridRoom = useMemo(
    () => gridRooms.find((room) => String(room.SpotId) === String(selectedSpot)),
    [gridRooms, selectedSpot]
  );

  // Existing bookings for the selected room, as FullCalendar events.
  const gridEvents = useMemo(() => {
    if (!gridRoom) return [];

    const dateStr = formatDate(selectedDate);

    return (gridRoom.SelectedBookingTime || []).reduce((events, entry, index) => {
      const slots = entry?.BookedSlot;

      if (!Array.isArray(slots) || slots.length === 0) return events;

      // A booking arrives as its consecutive 30-minute slots; the event spans
      // from the first slot's start to the last slot's end.
      const startTime = String(slots[0]).split("-")[0].trim();
      const endTime = String(slots[slots.length - 1]).split("-")[1]?.trim();

      if (!startTime || !endTime) return events;

      events.push({
        id: `${gridRoom.SpotId}-${index}`,
        title: `${entry.MeetingName} (${entry.Organizer})`,
        start: `${dateStr}T${startTime}:00`,
        end: `${dateStr}T${endTime}:00`,
        backgroundColor: "#D9D9D9",
        textColor: "#000",
        borderColor: "#fff",
        editable: false,
        overlap: false
      });

      return events;
    }, []);
  }, [gridRoom, selectedDate]);

  // Visible range of the grid. The room's own booking window when the grid API
  // has it, otherwise the zone's interval list, which is what the slot panel
  // shows - so the two never disagree about the day's bounds.
  const gridTimeRange = useMemo(() => {
    const toSlotTime = (value) =>
      value && String(value).length === 5 ? `${value}:00` : value;

    const firstPoint = timePoints[0];
    const lastPoint = timePoints[timePoints.length - 1];

    return {
      start: toSlotTime(gridRoom?.StartBookingTime || firstPoint) || "08:00:00",
      end: toSlotTime(gridRoom?.EndBookingTime || lastPoint) || "19:00:00"
    };
  }, [gridRoom, timePoints]);

  // #region Grid availability - start
  //
  // Same rules as NewMeetingModal's grid: a range is refused when it overlaps
  // one of the room's bookings or blocked times, or when its first slot has
  // already ended today. Checked against the grid API's own data, which is
  // what the user sees drawn.
  const gridBlockedTimes = useMemo(
    () => (gridRoom?.BlockedBookingTime || []).map((slot) => String(slot)),
    [gridRoom]
  );

  const rangesOverlap = (from, to, start, end) => !(to <= start || from >= end);

  // "yyyy-MM-ddTHH:mm:ss" -> "HH:mm", read after the "T" so it does not
  // depend on the date part's length.
  const eventTime = (value) => String(value).split("T")[1]?.slice(0, 5) || "";

  const isGridSlotBooked = (from, to) =>
    gridEvents.some((event) =>
      rangesOverlap(from, to, eventTime(event.start), eventTime(event.end))
    );

  const isGridSlotBlocked = (from, to) =>
    gridBlockedTimes.some((slot) => {
      const [blockedStart, blockedEnd] = slot.split("-").map((part) => part?.trim());

      if (!blockedStart || !blockedEnd) return false;

      return rangesOverlap(from, to, blockedStart, blockedEnd);
    });

  // Has the range's end passed, in the zone's own time? Same rule as
  // NewMeetingModal's hasSlotEnded(to), so a range still in progress stays
  // bookable. Step 1's validation uses it too, so step 1 and the grid agree.
  const hasRangeEnded = (from, to) =>
    isPastTimeInterval(`${from}-${to}`, zoneCurrentTime, selectedDate, zoneCurrentDate);

  const isGridRangeUnavailable = (from, to) => {
    if (!from || !to || from >= to) return true;

    return (
      hasRangeEnded(from, to) ||
      isGridSlotBooked(from, to) ||
      isGridSlotBlocked(from, to)
    );
  };

  // Grey out blocked slots, as NewMeetingModal does. DOM classes rather than
  // background events, so they sit under bookings without intercepting clicks.
  const applyBlockedTimeSlotClasses = () => {
    setTimeout(() => {
      const calendarEl = calendarRef.current?.getApi()?.el;

      if (!calendarEl) return;

      const blockedStartTimes = new Set(
        gridBlockedTimes
          .map((slot) => slot.split("-")[0]?.trim())
          .filter(Boolean)
          .map((time) => (time.length === 5 ? `${time}:00` : time))
      );

      calendarEl.querySelectorAll(".fc-timegrid-slot-lane").forEach((slot) => {
        const time = slot.getAttribute("data-time");

        slot.classList.toggle("fc-past-timezone", Boolean(time && blockedStartTimes.has(time)));
      });
    }, 0);
  };
  // #endregion Grid availability - end

  // The grid shows from the Room step through confirmation, and can be
  // picked on while the room or participants are being chosen. Confirmation is
  // a review screen, so there it only displays the selection.
  const showGrid =
    currentSection === SECTION.ROOM ||
    currentSection === SECTION.PARTICIPANTS ||
    currentSection === SECTION.CONFIRM;

  // NewBookingUI hands in a node outside .new-booking-tab-inner; the grid is
  // portalled there so it keeps this component's state and handlers. Without
  // one it renders inline.
  const placeTimeGrid = (grid) =>
    timeGridContainer ? createPortal(grid, timeGridContainer) : grid;

  const isGridLoading = gridLoading || spotsLoading;

  const isGridInteractive =
    (currentSection === SECTION.ROOM || currentSection === SECTION.PARTICIPANTS) &&
    !isGridLoading &&
    Boolean(selectedSpot);

  useEffect(() => {
    applyBlockedTimeSlotClasses();
  }, [gridBlockedTimes, showGrid, gridTimeRange.start, gridTimeRange.end]);

  // Mirror selectedFrom/selectedTo onto the grid, so a time picked on step 1
  // or on the grid shows as the same green block ("User selection" in the
  // design). showGrid and selectedSpot are dependencies because the grid
  // (re)mounts on entering step 2 and on every room change (its key).
  useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (!calendarApi) return;

    if (
      !selectedFrom ||
      !selectedTo ||
      selectedFrom === "00:00" ||
      selectedTo === "00:00"
    ) {
      calendarApi.unselect();
      isProgrammaticSelection.current = false;
      return;
    }

    const dateStr = formatDate(selectedDate);

    // calendarApi.select() ignores `selectable` and `selectAllow`, so this
    // also paints the read-only grid on the confirmation screen.
    isProgrammaticSelection.current = true;
    calendarApi.unselect();
    calendarApi.select(
      new Date(`${dateStr}T${selectedFrom}:00`),
      new Date(`${dateStr}T${selectedTo}:00`)
    );
  }, [selectedFrom, selectedTo, selectedDate, gridTimeRange, showGrid, selectedSpot]);

  // Switching room keeps the time when the new room is free for it, and
  // clears it (with the usual "select time" error) when it is not - the grid
  // is then where a new range is picked.
  useEffect(() => {
    if (currentSection !== SECTION.ROOM && currentSection !== SECTION.PARTICIPANTS) return;
    if (!selectedSpot || gridLoading) return;
    if (!selectedFrom || !selectedTo || selectedFrom === "00:00" || selectedTo === "00:00") return;

    if (isGridRangeUnavailable(selectedFrom, selectedTo)) {
      setSelectedFrom("");
      setSelectedTo("");
      setErrors((prev) => ({ ...prev, timeInvalid: t("please_select_time") }));
    }
  }, [selectedSpot, gridRoom, gridLoading]);

  const applyGridRange = (from, to) => {
    setErrors((prev) => ({ ...prev, timeInvalid: "" }));
    setSelectedFrom(from);
    setSelectedTo(to);
    setShowSlots(false);
  };
  // #endregion Day time grid- end

  useEffect(() => {
    const basicInfo =
      bookingInfo?.MeetingRoomBookingBasicInfo;

    if (basicInfo) {
      setRepeatOptionsList(basicInfo.RepeatOption || []);
      setWeeklyOptionsList(basicInfo.WeeklyRepeatOption || []);
      // MonthlyRepeatOption is no longer read: the monthly rows are derived
      // from the selected date (useMeetingRecurrence), not from whichever
      // weekday ids the API happened to offer.
    }
  }, [bookingInfo]);

  useEffect(() => {
    const zones = bookingInfo?.ZoneList || [];

    if (zones.length > 0) {
      setSelectedZone(zones[0].Id);
    }
  }, [bookingInfo]);

  // The monthly-default effect is gone with the dropdown: it forced the value
  // away from 0 because 0 was not one of the dropdown's items. In the radio
  // group 0 is a first-class row ("On day N"), so the effect would immediately
  // undo the user's pick. useMeetingRecurrence now defaults the row instead.

  useEffect(() => {
    setStep(0);
  }, [selectedTab]);

  const tooltipRef = useRef();
  useEffect(() => {
    if (tooltipRef.current) {
      new Tooltip(tooltipRef.current);
    }
  }, []);

  // #region Add Participant - start

  const [employee, setEmployee] = useState("");
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  //const selectedEmployeeIds = PId + "," + selectedEmployees.map(e => e.EmployeeId).join(","); //  "Participants": for create new meeting API
  const selectedEmployeeIds =
    selectedEmployees && selectedEmployees.length > 0
      ? PId + "," + selectedEmployees.map(e => e.EmployeeId).join(",")
      : PId;
  const [checkedEmployees, setCheckedEmployees] = useState([]);
  const totalParticipants = 1 + selectedEmployees.length + externalGuests.length;

  const employeeOptions = employeeList
    ?.filter(
      (emp) =>
        !selectedEmployees.some(
          (sel) => String(sel.EmployeeId) === String(emp.EmployeeId)
        )
    )
    .map((emp) => ({
      value: String(emp.EmployeeId),
      label: emp.EmployeeEmail
        ? `${emp.EmployeeName} (${emp.EmployeeEmail})`
        : emp.EmployeeName,
    }));

  // add employee object to selectedEmployees (id from select)
  const addEmployeeById = (empId) => {
    if (!empId) return;

    const nextCount = totalParticipants + 1;

    if (capacity > 0 && nextCount > capacity) {
      setErrors((prev) => ({
        ...prev,
        capacity: t("react_maximum_capacity_reached")
      }));
      return;
    }

    const empObj = employeeList.find(
      (x) => String(x.EmployeeId) === String(empId)
    );

    if (!empObj) return;

    setSelectedEmployees((prev) => {
      if (prev.some((s) => s.EmployeeId === empObj.EmployeeId)) return prev;
      return [...prev, empObj];
    });

    setEmployee("");
  };

  const handleCheckboxChange = (empId) => {
    setCheckedEmployees((prev) =>
      prev.includes(empId)
        ? prev.filter((id) => id !== empId) // uncheck
        : [...prev, empId]                  // check
    );
  };
  const fetchEmployeesBySpotId = async (selectedSpot) => {
    if (!selectedSpot) return;
    try {
      //setIsLoading(true);
      const reqParams =
      {
        SpotId: selectedSpot,
        Search: "",
        SelectedParticipant: "",
      };

      const response = await GetParticipantsForMeeting_api(reqParams);
      console.log("Requesting participants with params:", reqParams);
      console.log("GetParticipantsForMeeting API response:", response);
      const participantList = response?.Data?.Participants || response?.Data?.Records?.Participants || [];

      const normalized = participantList.map((e) => ({
        EmployeeId: String(e.Id ?? e.EmployeeId ?? ""),
        EmployeeName: `${e.FirstName ?? ""} ${e.LastName ?? ""}`.trim(),
        EmployeeEmail: e.PrimaryEmail ?? e.Email ?? "",
      }));

      setEmployeeList(normalized);
    } catch (err) {
      console.error("Error fetching Employees:", err);
      setEmployeeList([]);
    }
    finally {
      //setIsLoading(false);
    }
  };


  useEffect(() => {
    if (selectedSpot) {
      fetchEmployeesBySpotId(selectedSpot);
    }
    else {
      setEmployeeList([]);
    }
  }, [selectedSpot, selectedZone]);

  useEffect(() => {
    setSelectedEmployees((prev) =>
      prev.filter((selectedEmployee) =>
        employeeList.some(
          (employee) => String(employee.EmployeeId) === String(selectedEmployee.EmployeeId)
        )
      )
    );

    setCheckedEmployees((prev) =>
      prev.filter((employeeId) =>
        employeeList.some((employee) => String(employee.EmployeeId) === String(employeeId))
      )
    );
  }, [employeeList]);
  const isCapacityReached = () => {
    return capacity > 0 && totalParticipants >= capacity;
  };


  useEffect(() => {
    if (capacity <= 0 || totalParticipants < capacity) {
      setErrors((prev) => ({
        ...prev,
        capacity: ""
      }));
    }
  }, [selectedEmployees, externalGuests, capacity, totalParticipants]);

  // #endregion Add Participant - end

  // #region Approver- start
  const [approvalInfo, setApprovalInfo] = useState("");
  const [approverList, setApproverList] = useState([]);
  const [requestWindow, setRequestWindow] = useState({ hours: "", minutes: "" });
  const [releaseWindow, setReleaseWindow] = useState({ hours: "", minutes: "" });
  const parseApprovalInfo = (text = "") => {
    const numericParts = String(text).match(/\d+/g) || [];

    return {
      request: {
        hours: numericParts[0] || "00",
        minutes: numericParts[1] || "00"
      },
      release: {
        hours: numericParts[2] || "00",
        minutes: numericParts[3] || "00"
      }
    };
  };

  useEffect(() => {
    if (currentSection === SECTION.APPROVAL && !needApproval) {
      setCurrentSection(SECTION.CONFIRM); // fallback
    }
  }, [currentSection, needApproval]);
  // #endregion Approver- end

  // #region Handlers- start

  const handleNext = () => {
    if (currentSection < SECTION.CONFIRM) {

      const isValid = validateSection(currentSection);

      if (!isValid) {
        // 🔥 Focus + Scroll ONLY when meeting name error
        if (currentSection === SECTION.DETAILS && (!meetingName || meetingName.trim() === "")) {

          setTimeout(() => {
            const input = meetingNameRef.current;

            if (input) {
              input.focus();

              const modalBody = document.querySelector("#newmeetingsModal .modal-body");

              if (modalBody) {
                modalBody.scrollTo({
                  top: input.offsetTop - 120,
                  behavior: "smooth"
                });
              }
            }
          }, 100);
        }

        return;
      }

      // Entering the Room step fetches rooms for the picked time. Flag the load
      // up front, so the "no space available" block does not flash on the
      // render before that fetch starts.
      if (currentSection === SECTION.DETAILS) {
        setSpotsLoading(true);
      }

      setCurrentSection((prev) => prev + 1);
      setStep(prev => prev + 1);
      setTimeout(() => {
        const modalBody = document.querySelector("#newmeetingsModal .modal-body");
        if (modalBody) modalBody.scrollTop = 0;
      }, 100);
    }
  };

  const handleBack = () => {
    if (currentSection > 0) {
      setCurrentSection((prev) => prev - 1);

      setStep(prev => prev - 1);
    }
  };

  // "GuestParticipants" handler for create new meeting API

  const emailPattern = EMAIL_PATTERN;
  const handleConfirmExternalGuest = (e) => {
    e?.preventDefault();

    // #region Validation for Guest Input - start
    let newErrors = {
      firstname: "",
      lastname: "",
      email: ""
    };

    let isValid = true;

    if (!(guestInput.name || "").trim()) {
      newErrors.firstname = t("please_enter_valid_firstname");
      isValid = false;
    }

    if (!(guestInput.surname || "").trim()) {
      newErrors.lastname = t("enter_lastname");
      isValid = false;
    }

    const email = (guestInput.email || "").trim();

    if (!email || !emailPattern.test(email)) {
      newErrors.email = t("enter_your_valid_email_address");
      isValid = false;
    }

    setErrors(newErrors);
    //return isValid;

    // #endregion Validation for Guest Input - end
    if (isValid) {
      const nextCount = totalParticipants + 1;
      if (capacity > 0 && nextCount > capacity) {
        setErrors((prev) => ({
          ...prev,
          capacity: t("react_maximum_capacity_reached")
        }));
        return;
      }

      const { name, surname, email, nationalIdentificationId } = guestInput;

      if (!(name || "").trim() || !(email || "").trim()) {
        return;
      }

      const guestKey = `guest-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      const newGuest = {
        GuestKey: guestKey,
        FirstName: name.trim(),
        LastName: surname.trim(),
        PrimaryEmail: email.trim(),
        Id: "0",
        NationalIdentificationId: (nationalIdentificationId || "").trim()
      };

      // append guest to externalGuests array (allow multiple)
      setExternalGuests((prev) => [...(Array.isArray(prev) ? prev : []), newGuest]);

      // add a UI participant object with a unique id (avoid key collisions when Id === "0")
      const participantForUI = {
        id: guestKey,
        name: `${newGuest.FirstName} ${newGuest.LastName}`.trim(),
        email: newGuest.PrimaryEmail || "",
      };
      setParticipants((prev) => [...(Array.isArray(prev) ? prev : []), participantForUI]);

      // Clear inputs
      setGuestInput({ name: "", surname: "", email: "", nationalIdentificationId: "" });

      // Collapse the guest input section
      const collapseEl = document.getElementById("collapseExample");
      if (collapseEl) {
        collapseEl.classList.remove("show");
        collapseEl.setAttribute("aria-expanded", "false");
      }
      console.log("✅ External guest added:", newGuest);
    }
  };

  const handleRemoveParticipant = (id) => {
    if (!checkedParticipants.includes(id)) return;

    setParticipants((prev) => prev.filter((p) => p.id !== id));
    setExternalGuests((prev) =>
      prev.filter((guest) => guest.GuestKey !== id)
    );

    setCheckedParticipants((prev) =>
      prev.filter((pid) => pid !== id)
    );
    setErrors((prev) => ({
      ...prev,
      capacity: ""
    }));
  };


  const handleParticipantCheckboxChange = (id) => {
    setCheckedParticipants((prev) =>
      prev.includes(id)
        ? prev.filter((pid) => pid !== id) // uncheck
        : [...prev, id]                    // check
    );
  };

  const handleRemoveEmployee = (empId) => {
    if (!checkedEmployees.includes(empId)) return;
    if (!checkedEmployees.includes(empId)) return;

    setSelectedEmployees((prev) => {
      const updated = prev.filter((e) => e.EmployeeId !== empId);

      return updated;
    });

    setCheckedEmployees((prev) =>
      prev.filter((id) => id !== empId)
    );
    setErrors((prev) => ({
      ...prev,
      capacity: ""
    }));
  };

  const handleConfirm = async () => {
    try {
      console.log("needApproval from handleconfirm-", needApproval)
      setIsLoading(true);
      // Approval section → just close
      if (currentSection === SECTION.APPROVAL) {
        //setTimeout(() => onClose(), 600); // close modal
        setIsLoading(true);
        //onClose();
        history.push("/booking");
        return;                           // stop here
      }
      // onClose()
      const reqParams = {
        //BookingDate: parsedDate          ? formatDate(parsedDate)          : formatDate(new Date()),
        BookingDate: selectedDate ? formatDate(selectedDate) : formatDate(new Date()),
        EndDate: endDate ? formatDate(endDate) : "",
        MeetingName: meetingName,
        SpotId: selectedSpot,
        StartTime: selectedFrom,
        EndTime: selectedTo,
        // v5.1 (RONLARAVEL-21): MonthlyRepeatOption is gone, replaced by
        // MonthlyRepeatType + RepeatInterval. This screen shares the endpoint
        // with NewMeetingModal, and used to send the pre-v5.1 shape - so a
        // monthly meeting booked here saved as Default and lost its weekday
        // pattern. Both now build the payload from useMeetingRecurrence.
        ...getRecurrencePayload(),
        Participants: selectedEmployeeIds,
        Note: notes,
        EndDateBy: getEndDateBy(endDate), // 2 when EndDate is sent, else 1
        GuestParticipants: externalGuests,
      };
      console.log("Event Parameters:", reqParams);
      const data = await CreateNewMeeting_api(reqParams);

      console.log("✅ Create Meeting API Response:", data);
      /* handleApiResponse(data, setNotification, t, {
        successMsg: 1,
        errorMsg: 1
      }); */
      handleApiUiResponse(data, {
        successToaster: 1,
        errorToaster: 1,
        alertPopup: 1
      });
      if (data?.Data?.ResponseCode === 200) {

        //alert("✅ Meeting created successfully!");

        //if (typeof onMeetingCreated === "function") {
        console.log("✅ onMeetingCreated go inside function check");
        //onMeetingCreated();
        setEndDate(null);
        //}
        //setTimeout(() => onClose(), 600); // close modal
        console.log("needApproval-", needApproval, "currentSection-", currentSection);
        if (needApproval && currentSection < SECTION.APPROVAL) {
          setIsLoading(false);
          // Approval required → show the approval notice section
          setTimeout(() => setCurrentSection(SECTION.APPROVAL), 200);
        }
        else {
          await delay(1000);
          setIsLoading(true);
          setTimeout(() =>
            history.push("/booking")
            , 200);

          // setTimeout(() => onClose(), 600); // close modal
        }
      }
      else {
        //alert((data?.Data?.ErrorMessage || "Something went wrong") + "\nselectedFrom = " + selectedFrom + "\nselectedTo = " + selectedTo);
        setIsLoading(false);

        console.log("Error occured!", data?.Data?.ErrorMessage);
      }
    } catch (error) {
      console.error("❌ Error creating meeting:", error);
      //alert("Error occurred while creating meeting.");
      setNotification({
        type: "error",
        title: t("fail"),
        message: error?.message || t("oops_something_went_wrong_try_again")
      });

    } finally {
      setIsLoading(false);
      //onClose();
    }
  };
  // MeetingStepProgress clamps internally, so the confirmation and approval
  // sections keep the last circle lit; currentSection is passed raw.
  // Both gate the Room step, where the room dropdown lives.
  const isNoSpaceAvailable =
    currentSection === SECTION.ROOM &&
    !spotsLoading &&
    spotList.length === 0;
  // Only "no room free for this time" disables Next. "Still loading" and "no
  // room picked" are validateSection's job, which can say what is at fault.
  const isNextDisabled = isNoSpaceAvailable;
  useEffect(() => {
    setMeetingStep(currentSection);
  }, [currentSection]);

  // #endregion Handlers- end 
  return (
    <div className="tab-content active">
      <MeetingStepProgress step={currentSection} activeTab="meeting" totalSteps={MEETING_PROGRESS_STEPS} />

      {/* ========== STEP 1: details ========== */}
      {currentSection === SECTION.DETAILS && (
        <div className="step active" data-step="0">

          <form>

            {/* Title */}
            <div className="form-row mb-4">
              <label className="popup-inner-title m-0">{t("title")}</label>
              <input
                type="text"
                ref={meetingNameRef}
                className={`form-control
                ${errors.meetingName ? "error-form" : ""}
                ${!errors.meetingName && meetingName ? "success-validation" : ""}
                `}
                value={meetingName}
                onChange={(e) => {
                  setMeetingName(e.target.value);
                  setErrors((prev) => ({ ...prev, meetingName: "" }));
                }}
              />

              {errors.meetingName && (
                <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.meetingName}</p></div>
              )}
            </div>

            {/* Zone. The room list and time points are per zone, and the
                header's credit card reads its ZoneCreditValue. */}
            <div className="mb-4">
              <label className="popup-inner-title">{t("zone")}</label>
              <CustomSearchableDropdown
                items={bookingInfo?.ZoneList || []}
                value={selectedZone}
                labelKey="ZoneName"
                valueKey="Id"
                searchable={false}
                placeholder={t("select_a_zone")}
                onChange={(val) => setSelectedZone(val)}
                noOptionsMessage={t("no_zone_available")}
              />
            </div>

            {/* Date */}
            <div className="date-picker-wrapper mb-4">
              <label className="popup-inner-title">{t("date")}</label>
              <CustomDatePicker
                selected={selectedDate}
                onChange={(d) => setSelectedDate(d || new Date())}
                minDate={new Date()}
                onKeyDown={(e) => e.preventDefault()}
                onChangeRaw={(e) => e.preventDefault()}
              />
            </div>

            {/* Time. The zone's time points, picked From then To. No room is
                chosen yet at this step - step 2 lists the rooms free for this
                range, and the grid there can refine it. */}
            <div className="custom-timepicker mb-4">
              <label className="popup-inner-title">{t("time")}</label>

              <div
                className={`selected-box bg-white ${selectedFrom && selectedTo && !(selectedFrom === "00:00" && selectedTo === "00:00")
                  ? "ctm-success"
                  : ""
                  }`}
              >
                <div
                  onClick={() => {
                    setActiveField("from");
                    setShowSlots(true);
                    setErrors((prev) => ({ ...prev, timeInvalid: "" }));
                  }}
                >
                  {t("from")} <span className={!selectedFrom || selectedFrom === "00:00" ? "text-grey" : ""}>{selectedFrom || "00:00"}</span>
                </div>

                <div className="text-center">
                  <img src={Images.RightBlackArrow} className="img-fluid" />
                </div>

                <div
                  onClick={() => {
                    setActiveField("to");
                    setShowSlots(true);
                    setErrors((prev) => ({ ...prev, timeInvalid: "" }));
                  }}
                >
                  {t("react_to")} <span className={!selectedTo || selectedTo === "00:00" ? "text-grey" : ""}>{selectedTo || "00:00"}</span>
                </div>
              </div>

              {errors.timeInvalid && (
                <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.timeInvalid}</p></div>
              )}

              {showSlots && (
                <div className="slot-container d-grid mb-3">
                  {timePoints.map((time, idx) => {
                    const hasFromValue = selectedFrom && selectedFrom !== "00:00";
                    const hasToValue = selectedTo && selectedTo !== "00:00";
                    const pointMinutes = toMinutes(time);

                    const isSelected =
                      hasFromValue &&
                      (
                        time === selectedFrom ||
                        (hasToValue && pointMinutes >= toMinutes(selectedFrom) && pointMinutes <= toMinutes(selectedTo))
                      );

                    return (
                      <div
                        key={idx}
                        className={`slot ${isSelected ? "active" : ""}`}
                        onClick={() => handleTimeClick(time)}
                      >
                        {time}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {spotTagList?.length > 0 && (
              <div className="mb-4">
                <label className="popup-inner-title mt-0">{t("tags")}</label>
                <div className="inline-btn-tags tags-btn d-flex flex-wrap">
                  {spotTagList.map((tag) => (
                    <button
                      key={tag.Id}
                      type="button"
                      className={selectedTags.includes(tag.Id) ? "selected" : ""}
                      onClick={() => toggleTag(tag.Id)}
                    >
                      {tag.Name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* 🔥 Recurrence */}
            <div className="mb-4">
              <label className="popup-inner-title">{t("recurrence")}</label>
              <CustomSearchableDropdown
                items={repeatOptionsList}
                value={repeatType}
                labelKey="Label"
                valueKey="Value"
                searchable={false}
                onChange={(val) => {
                  // Recurrence values and their per-type resets live in the
                  // shared hook; end date is this screen's own state.
                  changeRepeatType(val);
                  setEndDate(null);
                  setIsRepeatEnabled(false);
                  setErrors((prev) => ({ ...prev, endDate: "", recurrenceDay: "" }));
                }}
              />
            </div>

            {/* Repeat every N Week(s)/Month(s). Weekly and Monthly only - see
                REPEAT_INTERVAL_UNIT_KEY in useMeetingRecurrence for why Daily
                and Biweekly have no interval. */}
            {repeatIntervalOptions.length > 0 && (
              <div className="mb-4">
                <label className="popup-inner-title">{t("edit_meeting_repeat_every")}</label>
                <CustomSearchableDropdown
                  items={repeatIntervalOptions}
                  value={repeatInterval}
                  labelKey="Label"
                  valueKey="Value"
                  searchable={false}
                  onChange={(val) => setRepeatInterval(Number(val))}
                />
              </div>
            )}

            {/* Weekly */}
            {showsWeekdayChips && (
              <div className="mb-4">
                <div className="d-flex flex-wrap gap-2">
                  {weeklyOptionsList.map((day) => (
                    <button
                      type="button"
                      key={day.Value}
                      className={`repeat-day-btn ${isWeeklyDaySelected(day.Value) ? "active" : ""
                        }`}
                      onClick={() => {
                        toggleWeeklyDay(day.Value);
                        setErrors((prev) => ({
                          ...prev,
                          recurrenceDay: ""
                        }));
                      }}
                    >
                      {day.Label}
                    </button>
                  ))}
                </div>
                {errors.recurrenceDay && (
                  <div className="error-text">
                    <img src={Images.ErrorMsg}></img> <p>{errors.recurrenceDay}</p>
                  </div>
                )}
              </div>
            )}

            {/* Monthly "repeat on" rows, Outlook-style. Derived from the
                selected date, so they follow the date picker with no extra
                wiring; replaces the weekday-id dropdown that could not tell
                "fourth Monday" from "last Monday" apart. */}
            {monthlyRecurrenceChoices.length > 0 && (
              <div className="mb-4">
                <div className="participants-list mt-0 border-top-0 py-0 new-meeting-checkbox">
                  {monthlyRecurrenceChoices.map((choice) => (
                    <div
                      key={choice.id}
                      className={`form-check d-flex align-items-center border-0 p-0 mb-3 ${activeMonthlyChoiceId === choice.id ? "check-active" : ""}`}
                    >
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="monthlyRecurrenceChoice"
                        id={`booking-monthly-choice-${choice.id}`}
                        checked={activeMonthlyChoiceId === choice.id}
                        onChange={() => {
                          selectMonthlyChoice(choice);
                          setEndDate(null);
                        }}
                      />

                      <label
                        className="form-check-label ms-2"
                        htmlFor={`booking-monthly-choice-${choice.id}`}
                      >
                        {choice.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* End Date */}
            {repeatType !== 0 && (
              <>
                <div className="form-check form-switch require-booking-sec d-inline-flex align-items-center">
                  <label className="popup-inner-title mt-0 text-start p-0 flex-unset mb-0 fw-bold">{t("EndDate")}

                  </label>
                  <span
                    className="ms-2 me-3 position-relative"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    style={{ cursor: "pointer" }}
                  >
                    <img src={Images.TooltipIcon} alt="tooltip" />

                    {showTooltip && (

                      <div className="custom-tooltip-meeting position-absolute">
                        <div className="tooltip-title d-flex align-items-center gap-2">
                          <img src={Images.InfoIcon} className="img-fluid" />
                          <h6>{t("end_date")}</h6>
                        </div>
                        <div className="tooltip-desc">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <img src={Images.GreenToggle} className="img-fluid" />
                            <p>
                              <Trans
                                i18nKey="react_to_set_end_date_in_meeting"
                                components={{ 1: <span className="text-green" /> }}
                              />
                            </p>

                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <img src={Images.GrayToggle} className="img-fluid" />
                            <p>
                              <Trans
                                i18nKey="react_to_set_recurring_meeting"
                                components={{ 1: <span className="text-green" /> }}
                              />
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </span>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="calendar-sync"
                    name="syncCalendar"
                    checked={isRepeatEnabled}
                    onChange={(e) => {
                      setIsRepeatEnabled(e.target.checked);
                      if (!e.target.checked) setEndDate(null);
                      setErrors((prev) => ({
                        ...prev,
                        endDate: e.target.checked ? prev.endDate : ""
                      }));
                    }}
                  />
                </div>

                <div className={`date-picker-wrapper mb-4 ${!isRepeatEnabled ? 'disable-date' : ''}`}
                >
                  <CustomDatePicker
                    autoComplete="off"
                    selected={endDate}
                    onChange={(date) => {
                      setEndDate(date);
                      setErrors((prev) => ({ ...prev, endDate: "" }));
                    }}
                    disabled={!isRepeatEnabled}
                    minDate={selectedDate}
                    placeholderText={isRepeatEnabled ? "--" : t("indefinitely")}
                  />
                </div>
                {errors.endDate && (
                  <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.endDate}</p></div>
                )}
              </>
            )}

            {/* Notes */}
            <div className="mb-4">
              <label className="popup-inner-title">{t("description")}</label>
              <textarea
                className="form-control"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

          </form>

        </div>
      )}

      {/* ========== STEP 2: room, beside the day grid ========== */}
      {currentSection === SECTION.ROOM && (
        <div className="step active" data-step="1">
          <form>
            {/* Room. Only rooms free for the time picked on step 1 are
                listed (see fetchSpots); the grid beside it shows the chosen
                room's day. */}
            <div className="mb-4">
              <label className="popup-inner-title mt-0">{t("Room")}</label>
              <CustomSearchableDropdownTickmark
                items={spotList}
                value={selectedSpot}
                labelKey="SpotName"
                valueKey="Id"
                searchable={false}
                disabled={spotList.length === 0}
                placeholder={
                  spotList.length === 0
                    ? t("no_space_available")
                    : t("select") + " " + t("space")
                }
                onChange={(value) => {
                  setSelectedSpot(value);
                  const nextSpot = spotList.find((item) => item.Id === value);

                  if (nextSpot) {
                    setIsRoomHasApprover(nextSpot.IsRoomHasApprover || 0);
                    setIsFullyDayBlocked(nextSpot.IsFullyDayBlocked || 0);
                    setCapacity(nextSpot.Capacity || 0);
                  }

                  setErrors((prev) => ({ ...prev, room: "" }));
                }}
              />

              {errors.room && (
                <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.room}</p></div>
              )}
            </div>

            {isNoSpaceAvailable && (
              <div className="no-space-found text-center mt-3 mb-4">
                <img
                  src={Images.NoSpaceError}
                  className="img-fluid mb-2"
                  style={{ maxWidth: "160px" }}
                />

                <p className="text-red fs-5">
                  {t("react_new_booking_no_available_spaces")}
                </p>

                <p className="">
                  {t("react_new_booking_please_update_selection")}
                </p>
              </div>
            )}
          </form>
        </div>
      )}

      {/* ========== STEP 3: participants, beside the day grid ========== */}
      {currentSection === SECTION.PARTICIPANTS && (
        <div className="step active" data-step="2">
          <form>
            <div className=" mb-4">
              <label className="popup-inner-title mt-0">{t("add_employee")}</label>

              <CustomSearchableDropdown
                items={employeeOptions || []}
                value={employee}  // string
                labelKey="label"
                valueKey="value"
                searchable={true}
                placeholder={t("name")}
                onChange={(empId) => {  // empId is string
                  //console.log("Selected employee ID:", empId);
                  setEmployee(empId);    // store string ID
                  addEmployeeById(empId);
                }}
              />

              {!showGuestForm && errors.capacity && (
                <div className="error-text">
                  <img src={Images.ErrorMsg} alt="" />
                  <p>
                    {errors.capacity}
                  </p>
                </div>
              )}
            </div>
            <div className="modal-btn my-4">
              <a
                className="green-filled-with-icon"
                role="button"
                onClick={() => setShowGuestForm(prev => !prev)}
              >
                <span><img src={Images.GreenPlus} /></span>
                {t("react_add_external_guest_at_new_booking")}
              </a>

              {showGuestForm && (
                <div className="card border-0 mt-3 ">
                  <div className="form-row mb-4">
                    <label className="popup-inner-title m-0" htmlFor="name">{t("firstName") + "*"}</label>
                    <input
                      type="text"
                      //className={`form-control ${errors.firstname ? "error-form" : ""}`}
                      className={`form-control 
                      ${errors.firstname ? "error-form" : ""}
                      ${!errors.firstname && guestInput.name ? "success-validation" : ""}
                      `}
                      autoComplete="off"
                      placeholder={t("firstName")}
                      value={guestInput.name}
                      onChange={(e) => {
                        setGuestInput({ ...guestInput, name: e.target.value });
                        setErrors((prev) => ({ ...prev, firstname: "" }));
                      }}
                    />

                    {errors.firstname && (

                      <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.firstname}</p></div>
                    )}
                  </div>
                  <div className="form-row mb-4">
                    <label className="popup-inner-title m-0">{t("lastName") + "*"}</label>
                    <input
                      type="text"
                      //className={`form-control ${errors.lastname ? "error-form" : ""}`}
                      className={`form-control 
                      ${errors.lastname ? "error-form" : ""}
                      ${!errors.lastname && guestInput.surname ? "success-validation" : ""}
                      `}
                      autoComplete="off"
                      placeholder={t("lastName")}
                      value={guestInput.surname}
                      onChange={(e) => {
                        setGuestInput({ ...guestInput, surname: e.target.value });
                        setErrors((prev) => ({ ...prev, lastname: "" }));
                      }}
                    />
                    {errors.lastname && (

                      <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.lastname}</p></div>
                    )}
                  </div>
                  <div className="form-row mb-4">
                    <label className="popup-inner-title m-0">{t("email") + "*"}</label>
                    <div className="position-relative">
                    <input
                      type="email"
                      className={`form-control 
                              ${errors.email ? "error-form" : ""}
                              ${!errors.email && guestInput.email ? "success-validation" : ""}
                              `}
                      placeholder={t("email")}
                      value={guestInput.email}
                      onChange={(e) => {
                        const value = e.target.value;

                        setGuestInput({ ...guestInput, email: value });

                        // ✅ Remove error immediately when user types
                        setErrors((prev) => ({ ...prev, email: "" }));
                      }}
                    />

                    <img
                      src={Images.EmailMessage}
                      // Hidden while the input shows its own right-side icon - the red error icon
                      // (error-form) or the green success tick (success-validation) - so they don't overlap
                      className={`form-icon ${errors.email || guestInput.email ? "d-none" : ""}`}
                    />
                    {errors.email && (

                      <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.email}</p></div>
                    )}
                    </div>
                  </div>
                  <div className="form-row mb-4">
                    <label className="popup-inner-title m-0" htmlFor="name">{t("national_id")} ({t("optional")})</label>
                    <input
                      type="text"
                      className="form-control"
                      autoComplete="off"
                      placeholder={t("national_id") + " (" + t("optional") + ")"}
                      value={guestInput.nationalIdentificationId}
                      onChange={(e) => setGuestInput({ ...guestInput, nationalIdentificationId: e.target.value })}
                    />
                  </div>
                  {errors.capacity && (
                    <div className="error-text">
                      <img src={Images.ErrorMsg}></img>
                      <p>{errors.capacity}</p>
                    </div>
                  )}
                  <div className="modal-btn">
                    <a
                      href="#"
                      className="main-btn outline-green-btn mb-3"
                      onClick={handleConfirmExternalGuest}
                    >
                      {t("react_confirm_external_guest_at_new_booking")}
                    </a>

                    <a href="#collapseExample" role="button" aria-expanded="false" aria-controls="collapseExample" className="main-btn outline-gray-btn"
                      onClick={() => setShowGuestForm(false)} >
                      <img src={Images.ArrowsVerticalExpand} className="img-fluid" />{t("react_collapse_section_at_new_booking")}</a                           >
                  </div>

                </div>
              )}
            </div>

            {/*------------------All Participants-list------------------*/}
            <div className="form-row participants-list ">
              <label className="popup-inner-title m-0" >{t("participants")}</label>
              <div className="form-check bg-white">

                <label className="form-check-label first-check-label">
                  {FirstName} {LastName} ({memberEmail})
                </label>
              </div>
              {selectedEmployees.length > 0 &&
                selectedEmployees.map((emp) => (
                  <div key={emp.EmployeeId}
                    //className="form-check d-flex align-items-center bg-white"
                    className={`form-check d-flex align-items-center bg-white ${checkedEmployees.includes(emp.EmployeeId) ? "check-active" : ""}`}
                  >
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`emp-${emp.EmployeeId}`}
                      checked={checkedEmployees.includes(emp.EmployeeId)}
                      onChange={() => handleCheckboxChange(emp.EmployeeId)}
                    />

                    <label
                      className="form-check-label ms-2"
                      htmlFor={`emp-${emp.EmployeeId}`}
                    >
                      {emp.EmployeeEmail
                        ? `${emp.EmployeeName} (${emp.EmployeeEmail})`
                        : emp.EmployeeName}
                    </label>

                    <a
                      className={`btn btn-link p-0 ms-2 check-delete ${checkedEmployees.includes(emp.EmployeeId) ? "opacity-1" : "opacity-0"}`}
                      //className="btn btn-link p-0 ms-2 check-delete"
                      onClick={() => handleRemoveEmployee(emp.EmployeeId)}
                      disabled={!checkedEmployees.includes(emp.EmployeeId)}
                      aria-label={`Remove ${emp.EmployeeName}`}
                    >
                      <img src={Images.Trash} className="img-fluid" alt="delete" />
                    </a>
                  </div>
                ))}

              {Array.isArray(participants) && participants.length > 0 &&
                participants.map((p) => (
                  <div
                    className={`form-check form-active d-flex align-items-center bg-white ${checkedParticipants.includes(p.id) ? "check-active" : ""
                      }`}
                    key={p.id}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`participant-${p.id}`}
                      checked={checkedParticipants.includes(p.id)}
                      onChange={() => handleParticipantCheckboxChange(p.id)}
                    />

                    <label
                      className="form-check-label ms-2"
                      htmlFor={`participant-${p.id}`}
                    >
                      {p.name} ({p.email})
                    </label>

                    <a
                      type="button"
                      className={`btn btn-link p-0 ms-2 check-delete ${checkedParticipants.includes(p.id) ? "opacity-1" : "opacity-0"}`}
                      onClick={() => handleRemoveParticipant(p.id)}
                      disabled={!checkedParticipants.includes(p.id)} // UX improvement
                      aria-label={`Remove ${p.name}`}
                    >
                      <img src={Images.Trash} className="img-fluid" alt="delete" />
                    </a>
                  </div>
                ))}

            </div>
          </form>
        </div>
      )}

      {/* STEP 4 */}
      {currentSection === SECTION.CONFIRM && (
        <div className="step active" data-step="2">

          <div className="modal-des">

            <div className="icon-with-list mb-0">
              <div className="icon-wrap mb-3">
                <img src={Images.Participant} className="img-fluid" alt="organizer" />
                <h4>{FirstName} {LastName}</h4>
              </div>

              <div className="icon-wrap mb-3">
                <img src={Images.GreenCalender} className="img-fluid" alt="date" />
                <h4>
                  {displaySelectedDate}
                </h4>

              </div>

              <div className="icon-wrap mb-3 align-items-start">
                <img src={Images.ZoneMap ?? Images.GreenCalender} className="img-fluid" alt="spot" />
                <h4>
                  {selectedSpotObj?.SpotName}
                  <span className="zone_text"> {meetingName}</span>
                </h4>
              </div>

              <div className="icon-wrap mb-3">
                <img src={Images.TimeIcon} className="img-fluid" alt="time" />
                <h4>
                  {t("from")} <span className="zone_text">{selectedFrom || "00:00"}</span> {t("react_to")} <span className="zone_text">{selectedTo || "00:00"}</span>
                </h4>
              </div>

              <div className="icon-wrap mb-5">
                <img src={Images.Participant} className="img-fluid" alt="participants" />
                <h4>
                  {`${1 + (participants?.length || 0) + (selectedEmployees?.length || 0)} ${t(
                    1 + (participants?.length || 0) + (selectedEmployees?.length || 0) === 1
                      ? "react_create_meeting_participant"
                      : "react_create_meeting_participants"
                  )}`}
                </h4>
              </div>
            </div>
            <div className="modal_img text-center pb-4">
              <img src={Images.Meeting} className="img-fluid" alt="meeting" />
            </div>
          </div>
        </div>
      )}
      {/* STEP 4 */}

      {needApproval && currentSection === SECTION.APPROVAL && (
        <div className="step active" data-step="3">
          <div className="modal-des">
            <div className="icon-with-list mb-0">
              {/* APPROVER */}
              <div className="icon-wrap mb-3">
                <img src={Images.Participant} className="img-fluid" />
                <h4>
                  <span className="zone_text">
                    {t(
                      approverList.join(", ").includes(",")
                        ? "react_create_meeting_approvers"
                        : "react_create_meeting_approver"
                    ).split("%s")[0]}
                  </span>
                  {approverList.join(", ")}
                  <span className="zone_text">
                    {t(
                      approverList.join(", ").includes(",")
                        ? "react_create_meeting_approvers"
                        : "react_create_meeting_approver"
                    ).split("%s")[1] || ""}
                  </span>
                </h4>
              </div>
              {/* DATE */}
              <div className="icon-wrap mb-3">
                <img src={Images.GreenCalender} className="img-fluid" />
                <h4>
                  {displaySelectedDate}</h4>
              </div>
              {/* APPROVAL INFO */}
              <div className="icon-wrap d-flex align-items-start">
                <img src={Images.TimeIcon} className="img-fluid mt-1" />
                {/* <h4><span className="zone_text d-block mb-1">Requests must be made at least 02 hours before start time. </span>An unapproved request will be released 01 hour before start time</h4> */}
                {/* <h4><span className="zone_text d-block mb-1"></span>{approvalInfo}</h4> */}
                <h4>
                  <span className="zone_text d-block mb-1">
                    {/* Requests must be made at least {requestWindow.hours} hours, {requestWindow.minutes} minutes before start time. */}
                    <span className="zone_text d-block mb-1">
                      {formatReactString(
                        t("meeting_room_requires_approval_from_assigned_approver_heading2"),
                        [requestWindow.hours, requestWindow.minutes]
                      )}
                    </span>
                    <span className="zone_text d-block mb-1" style={{ color: "#000" }}>

                      {formatReactString(
                        t("meeting_room_requires_approval_from_assigned_approver_heading3"),
                        [releaseWindow.hours, releaseWindow.minutes]
                      )}
                    </span>
                  </span>


                </h4>

              </div>
            </div>
            <div className="modal_img text-center mb-3 pb-1"><img src={Images.Unapproved} className="img-fluid" /></div>
          </div>
        </div>
      )}
      {/* Day grid, from the Room step through confirmation. Portalled into
          NewBookingUI's container beside .new-booking-tab-inner and absolutely
          positioned into the gutter that the form column reserves to its
          right. Selectable on Room and Participants (isGridInteractive),
          display-only on confirmation. */}
      {showGrid && placeTimeGrid(
        <div className="new-booking-time-grid">
          <div
            ref={setCalendarScrollEl}
            className="new-booking-time-grid-scroll"
            style={{
              pointerEvents: isGridLoading ? "none" : "auto",
              opacity: isGridLoading ? 0.65 : 1
            }}
          >
            <FullCalendar
              key={`${formatDate(selectedDate)}-${selectedSpot}-${gridTimeRange.start}-${gridTimeRange.end}`}
              ref={calendarRef}
              plugins={[timeGridPlugin, interactionPlugin]}
              initialView="timeGridDay"
              initialDate={selectedDate}
              height="auto"
              headerToolbar={false}
              // No current-time line: the grid-scoped border-color turns it
              // grey, so it reads as a highlighted slot. Past slots are already
              // greyed via fc-past-timezone.
              nowIndicator={false}
              allDaySlot={false}
              slotMinTime={gridTimeRange.start}
              slotMaxTime={gridTimeRange.end}
              selectable={isGridInteractive}
              unselectAuto={false}
              dayHeaders={true}
              dayHeaderContent={(arg) => t(WEEKDAY_LONG_KEYS[arg.date.getDay()])}
              events={gridEvents}
              selectAllow={(selectInfo) => {
                if (!isGridInteractive) return false;

                const from = selectInfo.startStr.slice(11, 16);
                const to = selectInfo.endStr.slice(11, 16);

                return !isGridRangeUnavailable(from, to);
              }}
              select={(info) => {
                const from = info.startStr.slice(11, 16);
                const to = info.endStr.slice(11, 16);

                // Ignore the echo of our own calendarApi.select() call. Checked
                // before the interactivity guard, so the flag is also cleared
                // on the display-only confirmation grid.
                if (isProgrammaticSelection.current) {
                  isProgrammaticSelection.current = false;

                  if (from === selectedFrom && to === selectedTo) return;
                }

                if (!isGridInteractive) return;

                ignoreNextDateClick.current = true;
                applyGridRange(from, to);
              }}
              dateClick={(info) => {
                if (!isGridInteractive) return;

                if (ignoreNextDateClick.current) {
                  ignoreNextDateClick.current = false;
                  return;
                }

                // A single click books the half hour it lands on.
                const from = info.dateStr.slice(11, 16);
                const to = minutesToTime(toMinutes(from) + 30);

                if (isGridRangeUnavailable(from, to)) return;

                applyGridRange(from, to);
              }}
              slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
              // The design labels hours "05.00"; the locale formatter gives "05:00".
              slotLabelContent={(arg) => arg.text.replace(":", ".")}
              eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
              viewDidMount={applyBlockedTimeSlotClasses}
            />
          </div>
        </div>
      )}

      {/* Footer Buttons */}

      <div
        className={`actions d-flex ${currentSection > 0
          ? "justify-content-between"
          : "justify-content-end"
          }  mt-3 `}
      >

        {/* BACK BUTTON */}
        {currentSection > 0 && (
          <button
            type="button"
            // Disabled on the approval notice: the booking already exists by
            // then, so there is nothing to go back and change.
            className={`main-btn outline-gray-btn ${currentSection === SECTION.APPROVAL ? "disable pe-auto" : ""}`}
            onClick={handleBack}
            disabled={currentSection === SECTION.APPROVAL}
          >
            {t("back")}
          </button>
        )}


        {/* NEXT BUTTON */}

        {currentSection < SECTION.CONFIRM && (
          <button
            type="button"
            className={`main-btn fill-green-btn ${isNextDisabled ? "green-disabled" : ""}`}
            onClick={handleNext}
            id="nextBtn"
            disabled={isNextDisabled}
          >
            {t("next")}
          </button>
        )}

        {/* CONFIRM BUTTON */}
        {(currentSection === SECTION.CONFIRM || currentSection === SECTION.APPROVAL) && (
          <button
            type="button"
            className="main-btn fill-green-btn"
            disabled={isLoading}
            //onClick={handleConfirm}
            onClick={() => {
              if (isLoading) return; // ✅ prevent click
              if (currentSection === SECTION.APPROVAL) {
                history.push("/booking");
              } else {
                //if (!isLoading) {
                handleConfirm();
                //}
              }
            }}
          >
            {t("ok")}
          </button>
        )}

      </div>
      {(notification) && (
        <NotificationMessage
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      {renderApiUi(isLoading)}
    </div>

  );
};

export default BookMeeting;
